import { HUB_EVENTS, hubEmitter } from '@/core/realtime';
import type { MessageReceiveEvent } from '@/core/realtime/hooks/use-message-receive-event';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { AckItem, AckMultipleDeliveredDto } from '@/features/messages/models/message-status';
import { messageStatusService } from '@/features/messages/services/message-status.service';
import { useEffect, useRef } from 'react';

export const useMessageDeliveryAck = () => {
  const ackedMessageIds = useRef<Set<string>>(new Set());
  const { user } = useAuthStore();

  useEffect(() => {
    if (!user) return;

    const handleMessageReceived = async (newMsg: MessageReceiveEvent) => {
      // Only ack messages that:
      // 1. Are not from current user
      // 2. Have messageId and conversationId
      // 3. Match current conversation
      if (
        newMsg.messageId &&
        newMsg.senderId &&
        newMsg.senderId !== user.user_id &&
        newMsg.conversationId &&
        !ackedMessageIds.current.has(newMsg.messageId)
      ) {
        // Ack immediately when receiving message from socket
        const ackItem = new AckItem(newMsg.conversationId, newMsg.messageId);
        ackedMessageIds.current.add(newMsg.messageId);

        try {
          await messageStatusService.ackDeliveredMessages(
            newMsg.conversationId,
            new AckMultipleDeliveredDto([ackItem])
          );
        } catch (error) {
          console.error('[MessageDeliveryAck] Failed to ack message:', error);
          // Remove from acked set on failure so it can be retried
          ackedMessageIds.current.delete(newMsg.messageId);
        }
      }
    };

    // Listen to socket message events instead of watching messages prop
    hubEmitter.on(HUB_EVENTS.MESSAGE.RECEIVED, handleMessageReceived);

    return () => {
      hubEmitter.off(HUB_EVENTS.MESSAGE.RECEIVED, handleMessageReceived);
    };
  }, [user?.user_id]);
};
